import {
  IconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type IPassengerCounts,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { Dialog, DialogContent, DialogTrigger, Icon } from "@b2bportal/core-ui";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  MAXIMUM_PASSENGERS_COUNT,
  PassengerCountPicker,
  useFlightPassengerSelection,
} from "@tenant/flights";
import clsx from "clsx";
import defaultStyles from "./FlightPassengerSelection.module.scss";

export const initialPaxCount: IPassengerCounts = {
  adultsCount: 1,
  childrenCount: 0,
  infantsInSeatCount: 0,
  infantsOnLapCount: 0,
};

export type TravelerPickerProps = PlatformComponentProps & {
  paxCount?: IPassengerCounts;
  disabled?: boolean;
  updatePaxCount: (count: IPassengerCounts) => void;
  unlimited?: boolean;
  maximumCount?: number;
  dialogClassName?: string;
  ariaLabel?: string;
};

export const FlightPassengerSelection = ({
  paxCount = initialPaxCount,
  disabled,
  updatePaxCount,
  dialogClassName,
  unlimited = false,
  maximumCount = MAXIMUM_PASSENGERS_COUNT,
  children,
  ariaLabel,
}: TravelerPickerProps) => {
  const { t } = useI18nContext();
  const { isDesktopAndUp } = useDeviceType();

  const styles = useFlightStyles(
    CoreFlightsComponent.FlightPassengerSelection,
    defaultStyles
  );
  const [, cn] = useModuleBEM(
    styles,
    CoreFlightsComponent.FlightPassengerSelection
  );
  const { context, handlers } = useFlightPassengerSelection({
    paxCount,
    updatePaxCount,
    dialogClassName,
    unlimited,
    maximumCount,
  });
  const {
    applyButtonText,
    maximumExceeded,
    travelerSelection,
    selectedPassengerLabel,
  } = context;
  const {
    handleOnChange,
    setOpenPassengerSelectionDialog,
    openPassengerSelectionDialog,
    setTravelerSelection,
  } = handlers;

  const handleOpenChange = (open: boolean) => {
    setOpenPassengerSelectionDialog(open);
    if (!open && !maximumExceeded) {
      handleOnChange();
    }
  };

  const handleApply = () => {
    handleOnChange();
    setOpenPassengerSelectionDialog(false);
  };

  const applyButton = (
    <div className={cn("apply-button-container")}>
      <button
        type="button"
        className={cn("apply-button")}
        onClick={handleApply}
        disabled={maximumExceeded}
      >
        {applyButtonText}
      </button>
    </div>
  );

  return (
    <Dialog
      open={openPassengerSelectionDialog}
      onOpenChange={handleOpenChange}
      modal={false}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn("button")}
          aria-label={ariaLabel}
        >
          <Icon
            iconName={IconNameEnum.passengerSelection}
            className={clsx(cn("icon"), cn("icon-start"))}
          />
          <div className={cn("label-container")}>
            <div className={cn("label")}>{t("travellers")}</div>
            <div className={cn("content")}>{selectedPassengerLabel}</div>
          </div>
        </button>
      </DialogTrigger>
      <DialogContent
        showOverlay={true}
        className={clsx(cn("dialog"), dialogClassName)}
        title={t("passengerCount.modalTitle")}
        fullScreen={!isDesktopAndUp}
        bottomFixedContent={applyButton}
      >
        <PassengerCountPicker
          minimumCount={1}
          setPassengerCounts={handleOnChange}
          onChange={setTravelerSelection}
          counts={travelerSelection}
          titles={{
            adultTitle: t("passengerCount.adultTitle"),
            adultSubtitle: t("passengerCount.adultSubtitle"),
            childrenTitle: t("passengerCount.childrenTitle"),
            childrenSubtitle: t("passengerCount.childrenSubtitle"),
            modalTitle: "",
            infantLapTitle: t("passengerCount.infantLapTitle"),
            infantLapSubtitle: t("passengerCount.infantLapSubtitle"),
            infantSeatTitle: t("passengerCount.infantSeatTitle"),
            infantSeatSubtitle: t("passengerCount.infantSeatSubtitle"),
          }}
        />
        {children}
      </DialogContent>
    </Dialog>
  );
};
